// Beskeder › skabeloner: kanal og "Hvornår bruges den?".
//  • Kanal: SMS, mail eller SMS + mail (én skabelon med begge varianter). Gamle skabeloner uden kanal: ud fra teksterne.
//  • Manuelt: skabelonen kan vælges, når kontoret sender en besked (Beskeder › Send besked, bookingen, kunden).
//  • Automatisk: skabelonen sendes af sig selv ved en hændelse – den bliver til en automatisering i det eksisterende system
//    (core/automation.ts: Workflow med Handling'er til kunden), som kører med de samme regler: betingelserne tjekkes igen lige
//    før afsendelse, påmindelser flytter med bookingen, ingen SMS om natten, samtykke til markedsføring og spærret udsendelse.
//    Automatiseringen har id'et wf_sk_<skabelon> og ses/rettes under Automatiseringer; skabelonen husker id'et (workflow_id).
import type { Betingelse, Forskydning, Haendelse, Handling, Skabelon, Workflow } from './automation.ts';

export type SkabelonKanal = 'sms' | 'mail' | 'begge';
export const KANALER: [SkabelonKanal, string][] = [['sms', 'SMS'], ['mail', 'Mail'], ['begge', 'SMS + mail']];

/** Skabelonens kanal – gemt, ellers ud fra teksterne (steamerens skabeloner er altid SMS). */
export function kanalFor(s: Pick<Skabelon, 'sms' | 'mail' | 'steamer'> & { kanal?: SkabelonKanal | null }): SkabelonKanal {
  if (s.steamer) return 'sms';
  if (s.kanal) return s.kanal;
  return s.mail.trim() ? (s.sms.trim() ? 'begge' : 'mail') : 'sms';
}
export const harSms = (k: SkabelonKanal) => k !== 'mail';
export const harMail = (k: SkabelonKanal) => k !== 'sms';

/** Hændelserne, en skabelon kan sendes ved. foer_start = før bookingens tid (fx dagen før); betaling_mangler = efter udført, hvis
 *  bookingen ikke er betalt. tid: 'nu' = med det samme eller senere; 'foer' = altid et stykke tid før. */
export type BrugHaendelse = 'booking_oprettet' | 'booking_godkendt' | 'booking_flyttet' | 'booking_aflyst' | 'foer_start' | 'booking_faerdig' | 'betaling_mangler' | 'betaling_modtaget';
export const BRUG_HAENDELSER: { id: BrugHaendelse; navn: string; tid: 'nu' | 'foer' }[] = [
  { id: 'booking_oprettet', navn: 'Når en booking er oprettet', tid: 'nu' },
  { id: 'booking_godkendt', navn: 'Når en booking er godkendt', tid: 'nu' },
  { id: 'booking_flyttet', navn: 'Når en booking er flyttet (ny tid)', tid: 'nu' },
  { id: 'booking_aflyst', navn: 'Når en booking er aflyst', tid: 'nu' },
  { id: 'foer_start', navn: 'Før bookingen (fx dagen før)', tid: 'foer' },
  { id: 'booking_faerdig', navn: 'Når opgaven er udført', tid: 'nu' },
  { id: 'betaling_mangler', navn: 'Når betalingen mangler efter udført', tid: 'nu' },
  { id: 'betaling_modtaget', navn: 'Når betalingen er modtaget', tid: 'nu' },
];

/** Automatisk brug: hændelsen, hvor længe før/efter (antal 0 = med det samme) og om det er service eller markedsføring. */
export type SkabelonBrug = { haendelse: BrugHaendelse; antal: number; enhed: Forskydning['enhed']; formaal: 'service' | 'markedsfoering'; aktiv: boolean };
export const STANDARD_BRUG: SkabelonBrug = { haendelse: 'foer_start', antal: 1, enhed: 'dage', formaal: 'service', aktiv: true };

export const workflowIdFor = (skabelonId: string) => ('wf_sk_' + skabelonId).slice(0, 60);

const MANGLER: Betingelse = { felt: 'betalt', op: 'er', vaerdier: ['nej'] };
const til = (h: BrugHaendelse): Haendelse => (h === 'foer_start' ? 'booking_start' : h === 'betaling_mangler' ? 'booking_faerdig' : h);

/** Tjek før gem. Fejl pr. felt (tom = ok). */
export function tjekBrug(b: SkabelonBrug): Record<string, string> {
  const f: Record<string, string> = {};
  if (!BRUG_HAENDELSER.some((x) => x.id === b.haendelse)) f.haendelse = 'Vælg hvornår den sendes.';
  const max = { minutter: 59 * 24 * 60, timer: 24 * 60, dage: 365 }[b.enhed];
  if (!Number.isInteger(b.antal) || b.antal < 0 || b.antal > max) f.antal = 'Skriv et helt tal.';
  else if (b.haendelse === 'foer_start' && b.antal < 1) f.antal = 'Skriv hvor længe før, fx 1 dag.';
  return f;
}

/** Skabelonen som automatisering: én handling pr. kanal til kunden. Findes automatiseringen allerede, bevares det, man har
 *  rettet dér (navn, sortering, målgruppe og egne betingelser), mens hændelse, tidspunkt, formål og teksterne kommer fra skabelonen. */
export function workflowFor(s: Skabelon & { id: string; kanal?: SkabelonKanal | null }, b: SkabelonBrug, gl?: Workflow | null): Workflow {
  const k = kanalFor(s);
  const handlinger: Handling[] = [
    ...(harMail(k) ? [{ id: 'a1', type: 'mail' as const, til: 'kunde' as const, aktiv: true, emne: s.emne, tekst: s.mail }] : []),
    ...(harSms(k) ? [{ id: 'a2', type: 'sms' as const, til: 'kunde' as const, aktiv: true, emne: '', tekst: s.sms }] : []),
  ];
  const egne = (gl?.betingelser ?? []).filter((x) => x.felt !== 'betalt');
  return {
    id: gl?.id ?? workflowIdFor(s.id), navn: gl?.navn ?? `Skabelon: ${s.navn}`, aktiv: b.aktiv, haendelse: til(b.haendelse), sortering: gl?.sortering ?? 99,
    formaal: b.formaal, ...(gl?.maalgruppe ? { maalgruppe: gl.maalgruppe } : {}),
    betingelser: b.haendelse === 'betaling_mangler' ? [...egne, MANGLER] : egne,
    forskydning: b.antal > 0 ? { antal: b.antal, enhed: b.enhed, retning: b.haendelse === 'foer_start' ? 'foer' : 'efter' } : null,
    handlinger,
  };
}

/** Automatiseringen tilbage til "Hvornår bruges den?" (null = den passer ikke til en skabelon, fx sluttid). */
export function brugFra(w: Workflow): SkabelonBrug | null {
  const f = w.forskydning;
  const formaal = w.formaal === 'markedsfoering' ? 'markedsfoering' : 'service';
  const basis = { antal: f?.antal ?? 0, enhed: f?.enhed ?? 'dage', formaal, aktiv: w.aktiv } as const;
  if (w.haendelse === 'booking_start') return f?.retning === 'foer' ? { ...basis, haendelse: 'foer_start' } : null;
  if (w.haendelse === 'booking_faerdig' && w.betingelser.some((x) => x.felt === 'betalt' && x.op === 'er' && x.vaerdier[0] === 'nej')) return { ...basis, haendelse: 'betaling_mangler' };
  const h = BRUG_HAENDELSER.find((x) => x.id === w.haendelse && x.tid === 'nu');
  return h && f?.retning !== 'foer' ? { ...basis, haendelse: h.id } : null;
}

const ENHED = { minutter: ['minut', 'minutter'], timer: ['time', 'timer'], dage: ['dag', 'dage'] } as const;
/** Kort tekst, fx "Sendes automatisk 1 dag før bookingen" eller "… med det samme, når opgaven er udført". */
export function brugTekst(b: SkabelonBrug): string {
  const h = BRUG_HAENDELSER.find((x) => x.id === b.haendelse)!;
  const tid = `${b.antal} ${ENHED[b.enhed][b.antal === 1 ? 0 : 1]}`;
  if (b.haendelse === 'foer_start') return `Sendes automatisk ${tid} før bookingen`;
  const naar = h.navn.charAt(0).toLowerCase() + h.navn.slice(1);
  return `Sendes automatisk ${b.antal ? `${tid} efter` : 'med det samme'}, ${naar}`;
}
